import { ANVIL_ACCOUNTS } from "@gotts.ai/testnet";
import { encodeFunctionData, parseUnits } from "viem";

import { MOCK_ERC20_ABI } from "../contracts/tokens/mock-erc20";
import { AGENT_VAULT_CORE_ABI } from "../contracts/vault/agent-vault-core";
import { FEE_MODULE_ABI } from "../contracts/vault/fee-module";

import type { DeployContext } from "../deployer/deploy-utils";
import type { VaultAddresses } from "../deployer/types";
import type { CreatedVault } from "./vaults";
import type { Address } from "viem";

export interface DepositRecord {
  /** Depositor label */
  depositor: string;
  /** Vault name */
  vaultName: string;
  /** Vault address */
  vault: Address;
  /** Amount deposited (human-readable) */
  amount: string;
  /** Asset symbol */
  asset: string;
}

export interface SeedDepositsResult {
  deposits: DepositRecord[];
  durationMs: number;
}

/**
 * Helper to approve and deposit into an identity-gated vault.
 *
 * Impersonates the depositor account, approves the vault to spend
 * the asset, then calls the identity-gated deposit(agentId, assets).
 * All seeded vaults have `identityGated: true`, so standard ERC-4626
 * deposit(assets, receiver) would revert.
 */
async function approveAndDeposit(
  ctx: DeployContext,
  depositor: Address,
  vault: Address,
  asset: Address,
  amount: bigint,
  agentId: number,
): Promise<void> {
  // Impersonate depositor
  await ctx.request({
    method: "anvil_impersonateAccount",
    params: [depositor],
  });

  // Approve vault to spend tokens
  const approveData = encodeFunctionData({
    abi: MOCK_ERC20_ABI,
    functionName: "approve",
    args: [vault, amount],
  });
  await ctx.walletClient.sendTransaction({
    account: depositor,
    to: asset,
    data: approveData,
    chain: ctx.walletClient.chain,
  });

  // Identity-gated deposit: deposit(agentId, assets)
  const depositData = encodeFunctionData({
    abi: AGENT_VAULT_CORE_ABI,
    functionName: "deposit",
    args: [BigInt(agentId), amount],
  });
  await ctx.walletClient.sendTransaction({
    account: depositor,
    to: vault,
    data: depositData,
    chain: ctx.walletClient.chain,
  });

  // Stop impersonating
  await ctx.request({
    method: "anvil_stopImpersonatingAccount",
    params: [depositor],
  });
}

/**
 * Seed deposits into the created vaults.
 *
 * Deposits:
 *   - Alice deposits 10,000 USDC into her vault
 *   - Bob deposits 5 WETH into his vault
 *   - Dave deposits 500 USDC into Alice's vault (cross-agent)
 *   - Carol deposits 1,000 USDC into her vault
 *
 * Then reports profit on Alice's vault and warps time to allow fee accrual.
 */
export async function seedDeposits(
  ctx: DeployContext,
  vaultAddresses: VaultAddresses,
  vaults: CreatedVault[],
  onAction?: (action: string) => void | Promise<void>,
): Promise<SeedDepositsResult> {
  const start = Date.now();
  const deposits: DepositRecord[] = [];

  const alice = ANVIL_ACCOUNTS[1];
  const bob = ANVIL_ACCOUNTS[2];
  const carol = ANVIL_ACCOUNTS[3];
  const dave = ANVIL_ACCOUNTS[4];

  if (!alice || !bob || !carol || !dave) {
    throw new Error("Required Anvil accounts not found");
  }

  const aliceVault = vaults.find((v) => v.agentId === 1);
  const bobVault = vaults.find((v) => v.agentId === 2);
  const carolVault = vaults.find((v) => v.agentId === 3);

  if (!aliceVault || !bobVault || !carolVault) {
    throw new Error("Required seed vaults not found");
  }

  // 1. Alice deposits 10,000 USDC into her vault (agentId=1)
  await onAction?.("Alice depositing 10,000 USDC into USDC Yield Vault");
  const aliceAmount = parseUnits("10000", 6);
  await approveAndDeposit(
    ctx,
    alice.address,
    aliceVault.vault,
    aliceVault.baseAsset,
    aliceAmount,
    1,
  );
  deposits.push({
    depositor: "alice",
    vaultName: aliceVault.name,
    vault: aliceVault.vault,
    amount: "10,000",
    asset: "USDC",
  });

  // 2. Bob deposits 5 WETH into his vault (agentId=2)
  await onAction?.("Bob depositing 5 WETH into WETH Growth Vault");
  const bobAmount = parseUnits("5", 18);
  await approveAndDeposit(
    ctx,
    bob.address,
    bobVault.vault,
    bobVault.baseAsset,
    bobAmount,
    2,
  );
  deposits.push({
    depositor: "bob",
    vaultName: bobVault.name,
    vault: bobVault.vault,
    amount: "5",
    asset: "WETH",
  });

  // 3. Dave deposits 500 USDC into Alice's vault (agentId=4, cross-agent)
  await onAction?.("Dave depositing 500 USDC into Alice's USDC Yield Vault");
  const daveAmount = parseUnits("500", 6);
  await approveAndDeposit(
    ctx,
    dave.address,
    aliceVault.vault,
    aliceVault.baseAsset,
    daveAmount,
    4,
  );
  deposits.push({
    depositor: "dave",
    vaultName: aliceVault.name,
    vault: aliceVault.vault,
    amount: "500",
    asset: "USDC",
  });

  // 4. Carol deposits 1,000 USDC into her vault (agentId=3)
  await onAction?.("Carol depositing 1,000 USDC into USDC Multi-Asset Vault");
  const carolAmount = parseUnits("1000", 6);
  await approveAndDeposit(
    ctx,
    carol.address,
    carolVault.vault,
    carolVault.baseAsset,
    carolAmount,
    3,
  );
  deposits.push({
    depositor: "carol",
    vaultName: carolVault.name,
    vault: carolVault.vault,
    amount: "1,000",
    asset: "USDC",
  });

  // 5. Simulate profit on Alice's vault: mint 100 USDC directly to the vault
  // (totalAssets = asset.balanceOf(this), so minting tokens = instant profit)
  await onAction?.("Simulating 100 USDC profit on Alice's vault");
  const profitAmount = parseUnits("100", 6);
  const mintData = encodeFunctionData({
    abi: MOCK_ERC20_ABI,
    functionName: "mint",
    args: [aliceVault.vault, profitAmount],
  });
  await ctx.walletClient.sendTransaction({
    account: ctx.deployer,
    to: aliceVault.baseAsset,
    data: mintData,
    chain: ctx.walletClient.chain,
  });

  // 6. Warp time by 1 day to allow fee accrual
  await onAction?.("Advancing time 1 day for fee accrual");
  await ctx.request({
    method: "evm_increaseTime",
    params: [86400], // 1 day in seconds
  });
  await ctx.request({
    method: "evm_mine",
    params: [],
  });

  // 7. Call feeModule.report() on Alice's vault
  await onAction?.("Calling feeModule.report() on Alice's vault");
  const feeReportData = encodeFunctionData({
    abi: FEE_MODULE_ABI,
    functionName: "report",
    args: [aliceVault.vault],
  });
  await ctx.walletClient.sendTransaction({
    account: ctx.deployer,
    to: vaultAddresses.feeModule,
    data: feeReportData,
    chain: ctx.walletClient.chain,
  });

  return {
    deposits,
    durationMs: Date.now() - start,
  };
}
